import { EmitterService } from '../../services/emitter.service';
import { onAnnotationDirtyStateChanged, onOpenAnnotation } from '../../services/annotation.service';
import { PencilBrush, Line, Triangle, Rect, Circle, Group, util, FabricImage, CanvasEvents, Canvas } from 'fabric';
import { CommentAnnotationPlugin } from '@mtm/conversation-client-sdk-annotation';
import { FabricEventGroupManager } from './fabric-event-group-manager';

export interface BaseDrawingHandlerParams {
  canvasFabric: any;
  strokeWidth?: number;
  triangleSize?: number;
  imageURL?: string;
  instanceId: string;
  annotationPlugin: CommentAnnotationPlugin;
}

export class BaseDrawingHandler {
  canvasFabric: any;
  objectIndex: number;
  redoIndex: number;
  objectStore: any[];
  redoStore: any[];
  undoClear: any;
  selectedObject: any;
  imageDragging: boolean = false;
  firstTouch: boolean = false;
  navigatorPlatform: string = 'unknown';
  protected static readonly drawingTools = ['rectangle', 'circle', 'arrow'];
  strokeWidth: number;
  triangleSize: number;
  imageURL: string;
  instanceId: string;
  annotationPlugin: CommentAnnotationPlugin;
  hoveredAnnotation: any;
  clickActive: boolean;
  // clickedAnnotation: any;
  currentColor: string = '#FF0000';
  protected temporaryDrawingId: string | null = null;
  private drawingDirty: boolean = false;
  private drawingShapeObject: any;
  private drawingLine: any;
  private drawingTriangle: any;
  private drawingDeltaX: number = 0;
  private drawingDeltaY: number = 0;
  private drawingIsDown: boolean = false;
  private drawingOrigX: number = 0;
  private drawingOrigY: number = 0;

  // List of possible platform values for different browsers
  private static readonly MAC_PLATFORMS = ['MacIntel', , 'macOS'];
  private static readonly WINDOWS_PLATFORMS = ['Win32', 'Win64', 'Windows'];

  // Event handler variables
  private objectAddedHandler: (this: Canvas, opt: CanvasEvents["object:added"]) => void;
  private objectRemovedHandler: (this: Canvas, opt: CanvasEvents["object:removed"]) => void;
  private objectModifiedHandler: (this: Canvas, opt: CanvasEvents["object:modified"]) => void;
  private objectScalingHandler: (this: Canvas, opt: CanvasEvents["object:scaling"]) => void;
  private drawingMouseDownHandler: (this: Canvas, opt: CanvasEvents['mouse:down']) => void;
  private drawingMouseMoveHandler: (this: Canvas, opt: CanvasEvents['mouse:move']) => void;
  private drawingMouseUpHandler: (this: Canvas, opt: CanvasEvents['mouse:up']) => void;
  private drawingPathCreatedHandler: (this: Canvas, opt: CanvasEvents['path:created']) => void;
  private annotationHoverMouseMoveHandler: (this: Canvas, opt: CanvasEvents['mouse:move']) => void;
  private annotationHoverMouseOutHandler: (this: Canvas, opt: CanvasEvents['mouse:out']) => void;
  private annotationClickMouseDownHandler: (this: Canvas, opt: CanvasEvents['mouse:down']) => void;
  protected canvasEventGroups = new FabricEventGroupManager(['core', 'drawing', 'hover', 'click'] as const);

  constructor(params: BaseDrawingHandlerParams) {
    this.canvasFabric = params.canvasFabric;
    this.canvasEventGroups.setCanvas(this.canvasFabric);
    this.strokeWidth = params.strokeWidth || 4;
    this.triangleSize = params.triangleSize || 20;
    this.imageURL = params.imageURL || '';
    this.instanceId = params.instanceId;
    this.annotationPlugin = params.annotationPlugin;

    this.detectOperatingSystem();
    this.initCanvas();
    this.setupEventHandlers();
    this.initUndoRedo();
    this.bindEvents();
    this.renderBaseShapesState();
    this.enableAnnotationHover();
    this.enableAnnotationClick();
    this.changeColor(this.currentColor);
  }

  get annotationMessages() {
    return this.annotationPlugin?.annotationState?.annotationMessages || [];
  }

  get clickedAnnotation() {
    return this.annotationPlugin?.annotationState?.activeAnnotation?.data || null;
  }

  initCanvas() {
    if (!this.canvasFabric.freeDrawingBrush) {
      this.canvasFabric.freeDrawingBrush = new PencilBrush(this.canvasFabric);
    }
  }

  setupEventHandlers() {
    const host = this;

    this.objectAddedHandler = function (this: Canvas, opt: CanvasEvents['object:added']) {
      host.onObjectAdded(this, opt);
    };
    this.objectRemovedHandler = function (this: Canvas, opt: CanvasEvents['object:removed']) {
      host.onObjectRemoved(this, opt);
    };
    this.objectModifiedHandler = function (this: Canvas, opt: CanvasEvents['object:modified']) {
      host.onObjectModified(this, opt);
    };
    this.objectScalingHandler = function (this: Canvas, opt: CanvasEvents['object:scaling']) {
      host.onObjectScaling(this, opt);
    };
    this.drawingMouseDownHandler = function (this: Canvas, opt: CanvasEvents['mouse:down']) {
      host.onDrawingMouseDown(this, opt);
    };
    this.drawingMouseMoveHandler = function (this: Canvas, opt: CanvasEvents['mouse:move']) {
      host.onDrawingMouseMove(this, opt);
    };
    this.drawingMouseUpHandler = function (this: Canvas, opt: CanvasEvents['mouse:up']) {
      host.onDrawingMouseUp(this, opt);
    };
    this.drawingPathCreatedHandler = function (this: Canvas, opt: CanvasEvents['path:created']) {
      host.onDrawingPathCreated(this, opt);
    };
    this.annotationHoverMouseMoveHandler = function (this: Canvas, opt: CanvasEvents['mouse:move']) {
      host.onAnnotationHoverMouseMove(this, opt);
    };
    this.annotationHoverMouseOutHandler = function (this: Canvas, opt: CanvasEvents['mouse:out']) {
      host.onAnnotationHoverMouseOut(this, opt);
    };
    this.annotationClickMouseDownHandler = function (this: Canvas, opt: CanvasEvents['mouse:down']) {
      host.onAnnotationClickMouseDown(this, opt);
    };
  }

  onObjectAdded(canvas: Canvas, opt: CanvasEvents['object:added']) {
    const target = opt.target as any;
    if (
        this.temporaryDrawingId &&
        target &&
        !target.id &&
        target.excludeFromExport === false &&
        target.type !== 'image'
      ) {
        target.set('id', this.temporaryDrawingId);
      }
    if (!target) return;

    if ('path' in target && target.path) {
      canvas.fire('object:modified', { target });
    }

    if (target.type === 'path' || target.type === 'group') {
      target.set({
        hasControls: false,
        lockScalingX: true,
        lockScalingY: true,
        lockScalingFlip: true,
        lockUniScaling: true,
      });
      canvas.renderAll();
    }

    this.checkClearDisable();
  }

  onObjectRemoved(canvas: Canvas, opt: CanvasEvents['object:removed']) {
    this.checkClearDisable();
  }

  onObjectModified(canvas: Canvas, opt: CanvasEvents['object:modified']) {
    const target = opt.target as any;
    if (this.undoClear == false && target) {
      if (!target.excludeFromExport && !target.isStateObject) {
        this.objectStore.push(JSON.stringify(canvas));
        this.objectIndex += 1;
        this.checkUndoRedoDisable();
        this.checkClearDisable();
      }
    }
  }

  onObjectScaling(canvas: Canvas, opt: CanvasEvents['object:scaling']) {
    const obj = opt.target as any;

    if (obj?.type === 'circle') {
      const newRadius = obj.radius * obj.scaleX;
      obj.set({
        radius: newRadius,
        scaleX: 1,
        scaleY: 1,
      });
    } else if (obj?.type === 'rect') {
      const newWidth = obj.width * obj.scaleX;
      const newHeight = obj.height * obj.scaleY;
      obj.set({
        width: newWidth,
        height: newHeight,
        scaleX: 1,
        scaleY: 1,
      });
    } else if (obj && (obj.type === 'group' || obj.type === 'path')) {
      obj.set({
        scaleX: 1,
        scaleY: 1,
      });

      canvas.renderAll();

      if (canvas.getActiveObject() === obj) {
        const activeObj = canvas.getActiveObject();
        canvas.discardActiveObject();
        canvas.setActiveObject(activeObj);
      }
    }
  }

  private fabricCalcArrowAngle(x1: number, y1: number, x2: number, y2: number): number {
    let angle = 0;
    const x = x2 - x1;
    const y = y2 - y1;
    if (x === 0) {
      angle = y === 0 ? 0 : y > 0 ? Math.PI / 2 : (Math.PI * 3) / 2;
    } else if (y === 0) {
      angle = x > 0 ? 0 : Math.PI;
    } else {
      angle = x < 0 ? Math.atan(y / x) + Math.PI : y < 0 ? Math.atan(y / x) + 2 * Math.PI : Math.atan(y / x);
    }
    return (angle * 180) / Math.PI + 90;
  }

  onDrawingMouseMove(canvas: Canvas, opt: CanvasEvents['mouse:move']) {
    if (opt.e.altKey === true) {
      return;
    }
    if (!BaseDrawingHandler.drawingTools.includes(this.selectedObject)) {
      return;
    }
    if (!this.drawingIsDown) return;

    const pointer = canvas.getPointer(opt.e);
    pointer.x = Math.max(0, pointer.x);
    pointer.y = Math.max(0, pointer.y);
    pointer.x = Math.min(pointer.x, canvas.backgroundImage.width);
    pointer.y = Math.min(pointer.y, canvas.backgroundImage.height);

    if (this.selectedObject === 'arrow' && this.drawingLine && this.drawingTriangle) {
      this.drawingLine.set({
        x2: pointer.x,
        y2: pointer.y,
      });
      this.drawingTriangle.set({
        left: pointer.x + this.drawingDeltaX,
        top: pointer.y + this.drawingDeltaY,
        angle: this.fabricCalcArrowAngle(this.drawingLine.x1, this.drawingLine.y1, this.drawingLine.x2, this.drawingLine.y2),
      });
    }
    if (this.selectedObject === 'circle' && this.drawingShapeObject) {
      let r = Math.pow(Math.pow(this.drawingOrigX - pointer.x, 2) + Math.pow(this.drawingOrigY - pointer.y, 2), 0.5);
      r = Math.min(r, this.drawingShapeObject.left, canvas.backgroundImage.width - this.drawingShapeObject.left);
      r = Math.min(r, this.drawingShapeObject.top, canvas.backgroundImage.height - this.drawingShapeObject.top);
      this.drawingShapeObject.set({ radius: r });
    }
    if (this.selectedObject === 'rectangle' && this.drawingShapeObject) {
      if (this.drawingOrigX > pointer.x) {
        this.drawingShapeObject.set({ left: Math.abs(pointer.x) });
      }
      if (this.drawingOrigY > pointer.y) {
        this.drawingShapeObject.set({ top: Math.abs(pointer.y) });
      }
      this.drawingShapeObject.set({ width: Math.abs(this.drawingOrigX - pointer.x) });
      this.drawingShapeObject.set({ height: Math.abs(this.drawingOrigY - pointer.y) });
    }
    canvas.renderAll();
  }

  onDrawingMouseDown(canvas: Canvas, opt: CanvasEvents['mouse:down']) {
    const drawingCanvas = canvas as any;

    if (opt.e.altKey === true) {
      return;
    }

    this.redoIndex = 0;
    this.redoStore = [];

    if (BaseDrawingHandler.drawingTools.includes(this.selectedObject)) {
      this.drawingIsDown = true;

      let pointer = canvas.getPointer(opt.e);
      pointer.x = Math.max(0, pointer.x);
      pointer.y = Math.max(0, pointer.y);
      pointer.x = Math.min(pointer.x, canvas.backgroundImage.width);
      pointer.y = Math.min(pointer.y, canvas.backgroundImage.height);
      this.drawingOrigX = pointer.x;
      this.drawingOrigY = pointer.y;

      pointer = canvas.getPointer(opt.e);
      pointer.x = Math.max(0, pointer.x);
      pointer.y = Math.max(0, pointer.y);
      pointer.x = Math.min(pointer.x, canvas.backgroundImage.width);
      pointer.y = Math.min(pointer.y, canvas.backgroundImage.height);

      if (this.selectedObject === 'arrow') {
        const points = [pointer.x, pointer.y, pointer.x, pointer.y] as [number, number, number, number];
        this.drawingLine = new Line(points, {
          strokeWidth: this.strokeWidth,
          fill: drawingCanvas.drawColor,
          stroke: drawingCanvas.drawColor,
          hoverCursor: 'default',
          originX: 'center',
          originY: 'center',
        });

        const centerX = (this.drawingLine.x1 + this.drawingLine.x2) / 2;
        const centerY = (this.drawingLine.y1 + this.drawingLine.y2) / 2;
        this.drawingDeltaX = this.drawingLine.left - centerX;
        this.drawingDeltaY = this.drawingLine.top - centerY;

        this.drawingTriangle = new Triangle({
          left: this.drawingLine.get('x1') + this.drawingDeltaX,
          top: this.drawingLine.get('y1') + this.drawingDeltaY,
          originX: 'center',
          originY: 'center',
          selectable: false,
          angle: -45,
          width: this.triangleSize,
          height: this.triangleSize,
          fill: drawingCanvas.drawColor,
          hoverCursor: 'default',
        });
        canvas.add(this.drawingLine, this.drawingTriangle);
      } else if (this.selectedObject === 'rectangle') {
        this.drawingShapeObject = new Rect({
          left: this.drawingOrigX,
          top: this.drawingOrigY,
          originX: 'left',
          originY: 'top',
          width: pointer.x - this.drawingOrigX,
          height: pointer.y - this.drawingOrigY,
          angle: 0,
          fill: 'rgba(0,0,0,0)',
          stroke: drawingCanvas.drawColor,
          selectable: false,
          strokeWidth: this.strokeWidth,
          hoverCursor: 'default',
        });
        canvas.add(this.drawingShapeObject);
      } else if (this.selectedObject === 'circle') {
        this.drawingShapeObject = new Circle({
          left: this.drawingOrigX,
          top: this.drawingOrigY,
          originX: 'center',
          originY: 'center',
          width: Math.pow(Math.pow(pointer.x - this.drawingOrigX, 2) + Math.pow(pointer.y - this.drawingOrigY, 2), 0.5),
          angle: 0,
          selectable: false,
          fill: 'rgba(0,0,0,0)',
          stroke: drawingCanvas.drawColor,
          strokeWidth: this.strokeWidth,
          hoverCursor: 'default',
        });
        canvas.add(this.drawingShapeObject);
      }
    }

    if (this.selectedObject === 'pencil') {
      this.freeDrawTemp();
    }
  }

  onDrawingMouseUp(canvas: Canvas, opt: CanvasEvents['mouse:up']) {
    if (opt.e.altKey === true) {
      return;
    }
    if (BaseDrawingHandler.drawingTools.includes(this.selectedObject)) {
      this.drawingIsDown = false;
    }
    if (this.selectedObject === 'arrow' && this.drawingLine && this.drawingTriangle) {
      const group = new Group([this.drawingLine, this.drawingTriangle], {
        selectable: false,
        hoverCursor: 'default',
        lockScalingX: true,
        lockScalingY: true,
        hasControls: false,
      });
      canvas.remove(this.drawingLine);
      canvas.remove(this.drawingTriangle);
      canvas.add(group);
      canvas.fire('object:modified', { target: group });
    } else if (this.selectedObject === 'rectangle' && this.drawingShapeObject) {
      canvas.fire('object:modified', { target: this.drawingShapeObject });
    } else if (this.selectedObject === 'circle' && this.drawingShapeObject) {
      canvas.fire('object:modified', { target: this.drawingShapeObject });
    }
  }

  onDrawingPathCreated(canvas: Canvas, opt: CanvasEvents['path:created']) {
    const path = (opt as any).path;
    if (!path) return;

    path.selectable = false;
    this.drawingShapeObject = path;
    path.set({
      hoverCursor: 'cursor',
      lockScalingX: true,
      lockScalingY: true,
      lockScalingFlip: true,
      lockUniScaling: true,
      hasControls: false,
    });
  }

  onAnnotationHoverMouseMove(canvas: Canvas, opt: CanvasEvents['mouse:move']) {
    const pointer = canvas.getPointer(opt.e);
    const isInside = this.isInsideImage(pointer);
    if (opt.target && isInside && !this.hoveredAnnotation) {
      const { id } = opt.target as any;
      this.highlightAnnotation(id);
    } else if (!isInside && opt.target) {
      this.unhighlightAnnotation((opt.target as any)?.id);
    }
  }

  onAnnotationHoverMouseOut(canvas: Canvas, opt: CanvasEvents['mouse:out']) {
    if (opt.target) {
      this.unhighlightAnnotation((opt.target as any)?.id);
    }
  }

  onAnnotationClickMouseDown(canvas: Canvas, opt: CanvasEvents['mouse:down']) {
    const pointer = canvas.getPointer(opt.e);
    const isInside = this.isInsideImage(pointer);
    if (opt.target && isInside) {
      const { id } = opt.target as any;
      if (this.clickedAnnotation?.id === id) {
        this.clickOutAnnotation(id);
        return;
      }
      this.clickInAnnotation(id);
    } else if (this.clickedAnnotation && (opt.target as any)?.id) {
      this.clickOutAnnotation((opt.target as any).id);
    }
  }

  detectOperatingSystem(): void {
    let rawPlatform = '';

    if (('userAgentData' in navigator) as any) {
      rawPlatform = navigator['userAgentData']['platform'] || '';
    }

    if (!rawPlatform) {
      rawPlatform = navigator.platform || '';
    }

    // Standardize platform to "mac" or "windows"
    if (BaseDrawingHandler.MAC_PLATFORMS.includes(rawPlatform)) {
      this.navigatorPlatform = 'mac';
    } else if (BaseDrawingHandler.WINDOWS_PLATFORMS.includes(rawPlatform)) {
      this.navigatorPlatform = 'windows';
    } else {
      this.navigatorPlatform = 'other';
    }
  }

  initUndoRedo(init = false) {
    this.objectIndex = 0;
    this.redoIndex = 0;
    this.objectStore = [];
    this.redoStore = [];
    this.undoClear = false;
    const clearState = JSON.parse(JSON.stringify(this.canvasFabric));
    if (!init) {
      clearState.objects = [];
    }
    this.objectStore.push(JSON.stringify(clearState));
    this.objectIndex += 1;
    this.checkUndoRedoDisable();
    this.checkClearDisable();
  }

  checkUndoRedoDisable() {
    // Disables undo/redo when they cannot be done
    if (this.objectIndex <= 1) {
      $('#undoIcon-' + this.instanceId).addClass('disabledButtonChange');
      EmitterService.get(this.instanceId).emit('disableUndo');
    } else {
      $('#undoIcon-' + this.instanceId).removeClass('disabledButtonChange');
      EmitterService.get(this.instanceId).emit('enableUndo');
    }

    if (this.redoIndex == 0) {
      $('#redoIcon-' + this.instanceId).addClass('disabledButtonChange');
      EmitterService.get(this.instanceId).emit('disableRedo');
    } else {
      $('#redoIcon-' + this.instanceId).removeClass('disabledButtonChange');
      EmitterService.get(this.instanceId).emit('enableRedo');
    }
  }

  checkClearDisable() {
    const hasDrawableObjects = this.canvasFabric?.getObjects()?.some((obj) => !obj.excludeFromExport) ?? false;

    if (hasDrawableObjects) {
      $('#clearIcon-' + this.instanceId).removeClass('disabledButtonChange');
      EmitterService.get(this.instanceId).emit('enableClear');
    } else {
      $('#clearIcon-' + this.instanceId).addClass('disabledButtonChange');
      EmitterService.get(this.instanceId).emit('disableClear');
    }

    this.updateDrawingDirtyState(hasDrawableObjects);
  }

  enableSelectableObjects() {
    const _this2 = this;
    if (_this2.selectedObject === 'cursor') {
      _this2.canvasFabric.getObjects().forEach((obj) => {
        if (obj.id !== 'background' && !obj.excludeFromExport) {
          if (obj.controls) {
            Object.keys(obj.controls).forEach((control) => {
              // Only show corner controls for diagonal scaling
              if (
                control.includes('mtr') ||
                control.includes('ml') ||
                control.includes('mt') ||
                control.includes('mr') ||
                control.includes('mb')
              ) {
                obj.controls[control].visible = false;
              }
            });
          }

          // Disable scaling
          if (obj.type === 'group' || obj.type === 'path') {
            obj.set({
              selectable: true,
              lockScalingX: true,
              lockScalingY: true,
              lockScalingFlip: true,
              lockUniScaling: true,
              hasControls: false,
            });
          } else {
            obj.set({
              selectable: true,
              lockUniScaling: false,
              lockScalingX: true,
              lockScalingY: true,
            });
          }

          obj.hoverCursor = 'move';
          _this2.canvasFabric.renderAll();
        }
      });
      const currentState = JSON.stringify(_this2.canvasFabric);
      _this2.renderState(currentState);
    }
  }

  renderState(state, disableSelectableObjects = false) {
    let _this2 = this;
    // canvasFabric.remove(...canvasFabric.getObjects());
    _this2.canvasFabric.getObjects().forEach((object) => {
      if (!object.excludeFromExport) {
        _this2.canvasFabric.remove(object);
      }
    });
    FabricImage.fromURL(this.imageURL).then((img) => {
      img.set({
        id: 'background',
        selectable: false,
        hoverCursor: 'cursor',
      });
      _this2.canvasFabric.backgroundImage = img;
      _this2.canvasFabric.renderAll();
    });
    // Setting isStateObject to avoid considering tho
    util.enlivenObjects(JSON.parse(state).objects).then((objs: any[]) => {
      objs.forEach((o) => {
        o.set('isStateObject', true);

        // renderState stores hasControls as true for path and group objects
        if (o.type === 'path' || o.type === 'group') {
          o.set({
            hasControls: false,
            lockScalingX: true,
            lockScalingY: true,
            lockScalingFlip: true,
            lockUniScaling: true,
          });
        }
        o.objectCaching = !_this2.selectedObject;

        _this2.canvasFabric.add(o);
      });
      _this2.canvasFabric.renderAll();

      _this2.canvasFabric.getObjects().forEach((obj) => {
        obj.set({ isStateObject: false });
        if (disableSelectableObjects) {
          obj.set({ selectable: false, hoverCursor: 'cursor' });
        }
      });
    });
  }

  disableSelectableObjects() {
    let _this2 = this;
    if (_this2.selectedObject != 4) {
      _this2.canvasFabric.getObjects().forEach((obj) => {
        obj.selectable = false;
        obj.objectCaching = false;
      });
      _this2.canvasFabric.discardActiveObject();
      _this2.canvasFabric.renderAll();
    }
  }

  clearCanvas() {
    this.canvasFabric.getObjects().forEach((object) => {
      if (!object.excludeFromExport) {
        this.canvasFabric.remove(object);
      }
    });

    const currentState = JSON.stringify(this.canvasFabric);
    this.objectStore.push(currentState);
    this.objectIndex += 1;

    this.checkUndoRedoDisable();
    this.checkClearDisable();
  }

  undo() {
    const _this2 = this;
    if (_this2.objectIndex > 1) {
      _this2.objectIndex -= 1;
      _this2.renderState(_this2.objectStore[_this2.objectIndex - 1]);
      _this2.redoStore.push(_this2.objectStore[_this2.objectStore.length - 1]);
      _this2.redoIndex++;
      _this2.objectStore.pop();
      _this2.checkUndoRedoDisable();
      _this2.checkClearDisable();
    }
  }

  redo() {
    if (this.redoIndex > 0) {
      this.redoIndex -= 1;
      this.renderState(this.redoStore[this.redoIndex]);
      this.objectStore.push(this.redoStore[this.redoIndex]);
      this.objectIndex++;
      this.redoStore.pop();
      this.checkUndoRedoDisable();
      this.checkClearDisable();
    }
  }

  getCurrentShapes() {
    const shape = JSON.parse(JSON.stringify(this.canvasFabric));
    shape['viewportTransform'] = this.canvasFabric.viewportTransform;
    return shape;
  }

  changeColor(color) {
    this.canvasFabric.drawColor = color;
    this.canvasFabric.freeDrawingBrush.color = color;
    this.currentColor = color;
  }

  changeActiveTool(tool, enableDrawingInteractions = true) {
    const { canvasFabric } = this;

    if (enableDrawingInteractions && !this.firstTouch) this.startMouseEvents();
    if (tool === 'pencil') {
      this.selectedObject = 'pencil';
      this.freeDrawTemp();
      this.disableSelectableObjects();
    } else if (tool === 'arrow') {
      this.selectedObject = 'arrow';
      canvasFabric.isDrawingMode = false;
      this.disableSelectableObjects();
    } else if (tool === 'rectangle') {
      this.selectedObject = 'rectangle';
      canvasFabric.isDrawingMode = false;
      this.disableSelectableObjects();
    } else if (tool === 'circle') {
      this.selectedObject = 'circle';
      canvasFabric.isDrawingMode = false;
      this.disableSelectableObjects();
    } else if (tool === 'cursor') {
      this.selectedObject = 'cursor';
      canvasFabric.isDrawingMode = false;
      this.enableSelectableObjects();
    } else if (tool === 'none') {
      this.selectedObject = 'none';
      canvasFabric.isDrawingMode = false;
      this.disableSelectableObjects();
    } else if (tool === 'delete') {
      this.deleteSelectedObject();
    } else if (tool === 'undo') {
      this.undo();
    } else if (tool === 'redo') {
      this.redo();
    } else if (tool === 'clear') {
      this.clearCanvas();
    }
  }

  bindEvents() {
    const _this2 = this;

    $(document).on('click.' + this.instanceId, function (e: any) {
      const upperCanvas = $(_this2.canvasFabric.upperCanvasEl);
      const lowerCanvas = $(_this2.canvasFabric.lowerCanvasEl);
      const wrapperEl = $(_this2.canvasFabric.wrapperEl);

      const clickedOnCanvas =
        upperCanvas.is(e.target) ||
        lowerCanvas.is(e.target) ||
        wrapperEl.is(e.target) ||
        wrapperEl.has(e.target).length > 0;

      if (!clickedOnCanvas && _this2.canvasFabric.getActiveObject()) {
        _this2.canvasFabric.discardActiveObject();
        _this2.canvasFabric.renderAll();
      }
    });

    const { canvasFabric } = this;

    // Clear cavas and update currentActive accordingly

    //Initialising variables
    _this2.initUndoRedo();

    this.canvasEventGroups.add('core', 'object:added', this.objectAddedHandler);
    this.canvasEventGroups.add('core', 'object:removed', this.objectRemovedHandler);
    this.canvasEventGroups.add('core', 'object:modified', this.objectModifiedHandler);
    this.canvasEventGroups.add('core', 'object:scaling', this.objectScalingHandler);
  }

  startMouseEvents() {
    this.disableAnnotationHover();
    this.disableAnnotationClick();
    this.canvasEventGroups.clear('drawing');
    const _this2 = this;

    if (!_this2.firstTouch) {
      _this2.initUndoRedo(true);
      // if (!_this2.plugin.controls.uiState.adding && !_this2.plugin.controls.uiState.editing) _this2.plugin.controls.startAddNew();
      _this2.firstTouch = true;
    }

    $(document).on('keydown.' + this.instanceId, function (o) {
      if (o.key === 'Alt' && !_this2.imageDragging) {
        _this2.canvasFabric.isDrawingMode = false;
        _this2.imageDragging = true;
      }
      // Handle delete based on OS
      if (
        (_this2.navigatorPlatform === 'mac' && (o.key === 'Backspace' || o.key === 'Delete')) ||
        (_this2.navigatorPlatform !== 'mac' && o.key === 'Delete')
      ) {
        _this2.deleteSelectedObject();
      }
    });

    $(document).on('keyup.' + this.instanceId, function (o) {
      if (o.key === 'Alt' && _this2.imageDragging) {
        if (_this2.selectedObject === 'pencil') {
          _this2.canvasFabric.isDrawingMode = true;
        }
        _this2.imageDragging = false;
      }
    });

    // Events for triggering toolbars
    _this2.canvasEventGroups.add('drawing', 'mouse:down', _this2.drawingMouseDownHandler);
    _this2.canvasEventGroups.add('drawing', 'mouse:move', _this2.drawingMouseMoveHandler);
    _this2.canvasEventGroups.add('drawing', 'mouse:up', _this2.drawingMouseUpHandler);
    _this2.canvasEventGroups.add('drawing', 'path:created', _this2.drawingPathCreatedHandler);
  }

  enableAnnotationHover() {
    // Disable annotation hover if it is already enabled
    this.canvasEventGroups.clear('hover');
    this.canvasEventGroups.add('hover', 'mouse:move', this.annotationHoverMouseMoveHandler);
    this.canvasEventGroups.add('hover', 'mouse:out', this.annotationHoverMouseOutHandler);
  }

  disableAnnotationHover() {
    if (this.hoveredAnnotation) {
      this.unhighlightAnnotation(this.hoveredAnnotation.id);
      this.hoveredAnnotation = null;
      // _this2.plugin.primordial.activeAnnotation = null;
    }

    this.canvasEventGroups.clear('hover');
  }

  enableAnnotationClick() {
    if (this.clickActive) return;
    this.clickActive = true;

    // Event for clicking on object
    this.canvasEventGroups.add('click', 'mouse:down', this.annotationClickMouseDownHandler);
  }

  disableAnnotationClick() {
    if (!this.clickActive) return;
    this.clickActive = false;
    this.canvasEventGroups.clear('click');
    if (this.clickedAnnotation) {
      const candidate = this.clickedAnnotation as any;
      if (candidate && typeof candidate.clickClose === 'function') {
        candidate.clickClose();
      } else {
        const annotationData = candidate?.data ?? candidate;
        try {
          this.annotationPlugin?.fire?.('annotationClickedOut', {
            annotation: annotationData,
            itemId: this.annotationPlugin?.getItem?.().id,
            instanceId: this.annotationPlugin?.instanceId,
          });
        } catch (_) {
          // no-op fallback if plugin/fire is unavailable
        }
      }
    }
  }

  isInsideImage(pointer) {
    const { canvasFabric } = this;
    if (!canvasFabric.backgroundImage) {
      return;
    }
    const isInside = false;
    const imageCoords = canvasFabric.backgroundImage.calcACoords();

    const width = Math.abs(imageCoords.bl.x - imageCoords.br.x);
    const height = Math.abs(imageCoords.bl.y - imageCoords.tl.y);

    if (pointer.x < 0 || pointer.x > width || pointer.y < 0 || pointer.y > height) {
      return false;
    } else {
      return true;
    }
  }

  revertOpacity(annotationId: string) {
    const { canvasFabric } = this;

    canvasFabric.getObjects().forEach(function (obj) {
      if (obj.id == annotationId) {
        obj.set({
          opacity: 1,
        });
        canvasFabric.renderAll();
      }
    });
  }

  decreaseOpacity(annotationId: string) {
    const { canvasFabric } = this;
    canvasFabric.getObjects().forEach(function (obj) {
      if (obj.id == annotationId) {
        obj.set({
          opacity: 0.5,
        });
        canvasFabric.renderAll();
      }
    });
  }

  highlightAnnotation(annotationId: any, isClick = false) {
    const annotation = this.findAnnotation(annotationId);
    if (!annotation) return;
    this.revertOpacity(annotation.id);
    if (!isClick) {
      this.hoveredAnnotation = annotation;
      EmitterService.get('annotationHoveredIn').emit({
        annotation: annotation,
        itemId: this.annotationPlugin?.getItem?.().id,
        instanceId: this.annotationPlugin?.instanceId,
      });
    }
  }

  unhighlightAnnotation(annotationId: any, isClick = false) {
    const annotation = this.findAnnotation(annotationId);
    if (!annotation) return;
    this.decreaseOpacity(annotation.id);
    if (!isClick) {
      if (this.hoveredAnnotation?.id === annotation.id) {
        this.hoveredAnnotation = null;
      }
      EmitterService.get('annotationHoveredOut').emit({
        annotation: annotation,
        itemId: this.annotationPlugin?.getItem?.().id,
        instanceId: this.annotationPlugin?.instanceId,
      });
    }
  }

  // hoverOpen(annotationId: any) {
  //   const annotation = this.findAnnotation(annotationId);
  //   this.revertOpacity(annotation.id);
  //   const data = {
  //     annotation: annotation.data,
  //     itemId: this.annotationPlugin.getItem().id,
  //     instanceId: this.annotationPlugin.instanceId,
  //   };
  //   this.hoveredAnnotation = annotation;
  //   this.annotationPlugin.fire('annotationHoverIn', data);
  // }

  // hoverClose(annotationId: any) {
  //   const annotation = this.findAnnotation(annotationId);
  //   if (!annotation) return;
  //   this.decreaseOpacity(annotation.id);
  //   const data = {
  //     annotation: annotation.data,
  //     itemId: this.annotationPlugin.getItem().id,
  //     instanceId: this.annotationPlugin.instanceId,
  //   };
  //   this.hoveredAnnotation = null;
  //   this.annotationPlugin.fire('annotationHoverOut', data);
  // }


  findAnnotation(id: string) {
    return this.annotationMessages.find((a) => a.id === id);
  }

  deleteSelectedObject() {
    const activeObject = this.canvasFabric.getActiveObject();
    if (activeObject) {
      this.canvasFabric.remove(activeObject);
      this.canvasFabric.fire('object:modified', { target: activeObject });
    }
  }

  freeDrawTemp() {
    this.canvasFabric.isDrawingMode = true;
    this.canvasFabric.freeDrawingBrush.width = this.strokeWidth;
    this.canvasFabric.freeDrawingBrush.color = this.canvasFabric.drawColor;
  }

  clickInAnnotation(id: string) {
    const annotation = this.findAnnotation(id);
    if (!annotation) return;
    this.revertOpacity(annotation.id);
    this.disableAnnotationHover();
    onOpenAnnotation.emit({
      annotation,
      itemId: this.annotationPlugin.getItem().id,
      instanceId: this.annotationPlugin.instanceId,
    });
  }

  clickOutAnnotation(id: string) {
    const annotation = this.findAnnotation(id);
    if (!annotation) return;
    this.decreaseOpacity(annotation.id);
    this.enableAnnotationClick();
    const data = {
      messageId: annotation.id,
      itemId: this.annotationPlugin.getItem().id,
      instanceId: this.annotationPlugin.instanceId,
    };
    this.annotationPlugin.fire('annotationClickOut', data);
  }

  addAnnotationDrawings(id: string, shape: any, excludeFromExport = true, decreaseOnAdd = false, _resolution?: any) {
    if (!shape) return;

    const { canvasFabric } = this;

    let annotationData: any;
    if (typeof shape === 'string') {
      try {
        annotationData = JSON.parse(shape);
      } catch (err) {
        return;
      }
    } else {
      annotationData = shape;
    }

    if (!annotationData?.objects) {
      return;
    }

    if (!excludeFromExport) {
      canvasFabric.remove(...canvasFabric.getObjects());
    }

    util.enlivenObjects(annotationData.objects).then((objects: any[]) => {
      objects.forEach(function (o) {
        o.set('id', id);
        o.set('selectable', false);
        o.set('hoverCursor', 'cursor');
        o.set('excludeFromExport', excludeFromExport);
        canvasFabric.add(o);
      });
      canvasFabric.renderAll();

      // TODO: Possibly merge this with eliven to avoid another getObjects loop
      if (decreaseOnAdd) {
        this.decreaseOpacity(id);
      }
    });
  }

  removeAnnotationDrawings(id: string) {
    const { canvasFabric } = this;
    canvasFabric.getObjects().forEach(function (o) {
      if (o.id == id) {
        canvasFabric.remove(o);
      }
    });
    canvasFabric.renderAll();
  }

  renderBaseShapesState() {
    const annotationData = this.annotationMessages;

    annotationData.forEach((annotation) => {
      this.addAnnotationDrawings(annotation.id, annotation.shape, true, true);
    });
  }

  resetMode(renderAllDrawings = true, removeAllDrawings = true) {
    this.firstTouch = false;
    this.imageDragging = false;
    this.turnoffEvents();

    if (removeAllDrawings) {
      this.canvasFabric.getObjects().forEach((obj) => {
        if (obj.id !== 'background') {
          this.canvasFabric.remove(obj);
        }
      });
    } else {
      if (this.temporaryDrawingId) {
        this.canvasFabric.getObjects().forEach((obj) => {
          if (obj.id === this.temporaryDrawingId) {
            this.canvasFabric.remove(obj);
          }
        });
      }
    }

    this.canvasFabric.isDrawingMode = false;
    this.enableAnnotationHover();
    this.enableAnnotationClick();
    this.canvasFabric.renderAll();
    if (renderAllDrawings) this.renderBaseShapesState();
    this.checkClearDisable();
  }

  /**
   * Explicitly enable drawing interactions on the canvas.
   * Re-registers drawing listeners to ensure clean state before drawing.
   */
  enableDrawingInteractions(): void {
    this.turnoffEvents();
    this.startMouseEvents();
  }

  /**
   * Explicitly disable drawing interactions and return to view-only mode.
   * Keeps annotation hover/click active so existing shapes remain interactive.
   */
  disableDrawingInteractions(): void {
    this.turnoffEvents();
    if (this.canvasFabric) {
      this.canvasFabric.isDrawingMode = false;
    }
    if (!this.isDrawingDirty()) {
      this.enableAnnotationHover();
      this.enableAnnotationClick();
    }
  }

  turnoffEvents() {
    // Remove document event listeners
    $(document).off('keydown.' + this.instanceId);
    $(document).off('keyup.' + this.instanceId);
    $(document).off('click.' + this.instanceId);

    // Remove grouped canvas listeners via Fabric disposers
    this.canvasEventGroups.clear('drawing');
    this.disableAnnotationClick();
    this.disableAnnotationHover();
    this.firstTouch = false;
  }

  teardown() {
    this.turnoffEvents();
    this.canvasEventGroups.clear('core');
    // Reset state variables
    this.selectedObject = null;
    this.imageDragging = false;
  }

  isDrawingDirty(): boolean {
    return this.drawingDirty;
  }

  private updateDrawingDirtyState(isDirty: boolean): void {
    if (this.drawingDirty === isDirty) {
      return;
    }
    this.drawingDirty = isDirty;
    onAnnotationDirtyStateChanged.next({
      dirty: isDirty,
      instanceId: this.instanceId,
    });
  }
}
